// API utility for making requests to the backend
import { sanitizeUserFacingError } from './userFacingError';

// Normalize URL to remove trailing slashes before appending /api
const normalizeUrl = (url) => {
  if (!url) return '';
  // Remove trailing slashes
  const cleanUrl = url.replace(/\/+$/, '');
  return `${cleanUrl}/api`;
};

const API_BASE_URL = normalizeUrl(process.env.REACT_APP_API_URL);

// Log API URL in development for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', API_BASE_URL || 'NOT SET - API calls will fail!');
}

export const api = {
  get: async (endpoint, token = null) => {
    if (!API_BASE_URL) {
      const error = new Error('REACT_APP_API_URL is not set. Please configure it in your environment variables.');
      console.error('API Error:', error.message);
      throw new Error(sanitizeUserFacingError(error.message, 'Unable to connect. Please try again.'));
    }

    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = endpoint.startsWith('/') ? `${API_BASE_URL}${endpoint}` : `${API_BASE_URL}/${endpoint}`;
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetching: ${url}`);
      }
      const response = await fetch(url, {
        method: 'GET',
        headers,
        cache: endpoint.includes('/products') || endpoint.includes('/categories') || endpoint.includes('/content/banners') || endpoint.includes('/content/product-sections') || endpoint.includes('/content/google-reviews') ? 'no-store' : 'default',
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(`Response status: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error [${response.status}]:`, errorText);
        if (process.env.NODE_ENV === 'development') {
          console.error('Request URL:', url);
          console.error('Response headers:', Object.fromEntries(response.headers.entries()));
        }
        let apiMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
          const parsed = errorText ? JSON.parse(errorText) : null;
          if (parsed?.message) apiMessage = parsed.message;
          else if (typeof parsed?.error === 'string') apiMessage = parsed.error;
        } catch {
          if (errorText && errorText.length < 200 && !errorText.startsWith('<')) {
            apiMessage = errorText;
          }
        }
        throw new Error(sanitizeUserFacingError(apiMessage, 'Unable to load data. Please try again.'));
      }

      const data = await response.json();
      if (process.env.NODE_ENV === 'development') {
        const preview =
          data == null
            ? data
            : Array.isArray(data)
              ? `array(len=${data.length})`
              : typeof data === 'object'
                ? `keys=${Object.keys(data).slice(0, 12).join(',')}${Object.keys(data).length > 12 ? '…' : ''}`
                : typeof data;
        console.log(`OK ${response.status} ${url}`, preview);
      }
      return data;
    } catch (error) {
      console.error('Fetch Error:', error);
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed URL:', url);
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
      throw new Error(sanitizeUserFacingError(error.message, 'Unable to connect. Please try again.'));
    }
  },

  post: async (endpoint, data, token = null) => {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = endpoint.startsWith('/') ? `${API_BASE_URL}${endpoint}` : `${API_BASE_URL}/${endpoint}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      const msg = error.message || error.error || `API Error: ${response.statusText}`;
      throw new Error(sanitizeUserFacingError(msg, 'Request failed. Please try again.'));
    }

    return response.json();
  },

  put: async (endpoint, data, token = null) => {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = endpoint.startsWith('/') ? `${API_BASE_URL}${endpoint}` : `${API_BASE_URL}/${endpoint}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      const msg = error.message || error.error || `API Error: ${response.statusText}`;
      throw new Error(sanitizeUserFacingError(msg, 'Update failed. Please try again.'));
    }

    return response.json();
  },

  delete: async (endpoint, token = null) => {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = endpoint.startsWith('/') ? `${API_BASE_URL}${endpoint}` : `${API_BASE_URL}/${endpoint}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error(sanitizeUserFacingError(`API Error: ${response.statusText}`, 'Request failed. Please try again.'));
    }

    return response.json();
  },

  /**
   * POST with FormData (e.g. file upload for image analysis).
   * Uses same backend base URL as other API calls. Do not set Content-Type so browser sets multipart boundary.
   */
  postFormData: async (endpoint, formData, token = null) => {
    if (!API_BASE_URL) {
      const error = new Error('REACT_APP_API_URL is not set. Please configure it in your environment variables.');
      console.error('API Error:', error.message);
      throw new Error(sanitizeUserFacingError(error.message, 'Unable to connect. Please try again.'));
    }

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = endpoint.startsWith('/') ? `${API_BASE_URL}${endpoint}` : `${API_BASE_URL}/${endpoint}`;
    if (process.env.NODE_ENV === 'development') {
      console.log(`POST (FormData): ${url}`);
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: response.statusText };
      }
      console.error(`API Error [${response.status}]:`, errorData?.message || errorText);
      const msg = errorData?.message || errorData?.error || `Image analysis failed: ${response.statusText}`;
      throw new Error(sanitizeUserFacingError(msg, 'Image analysis failed. Please try again.'));
    }

    return response.json();
  },
};

export default api;
export { API_BASE_URL };

